import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDropzone } from 'react-dropzone';
import { X, Upload, Check, Plus, Loader2, AlertCircle, FileText, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import AddCompanyModal from './AddCompanyModal';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';

import {
  jobSchema,
  defaultValues,
  JOB_STATUSES,
  EMPLOYMENT_TYPES,
  EXPERIENCE_LEVELS,
  LOCATION_REQUIREMENTS,
  CURRENCIES,
  TRAVEL_OPTIONS,
  YES_NO_OPTIONS
} from '../utils/jobValidation';

const JobCreationModal = ({ isOpen, onClose, onSuccess, editingJob = null, selectedUser, teamLead, onNavigateToCompanies }) => {
  const isEditMode = !!editingJob;
  const [showUploader, setShowUploader] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parseStatus, setParseStatus] = useState(null); // 'success', 'partial', 'error'
  const [parseMessage, setParseMessage] = useState('');
  const [currentSkill, setCurrentSkill] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [showRefinedSuggestions, setShowRefinedSuggestions] = useState(false);
  const [refinedData, setRefinedData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [companyAssignmentInfo, setCompanyAssignmentInfo] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    setValue,
    watch
  } = useForm({
    resolver: zodResolver(jobSchema),
    defaultValues: defaultValues,
    mode: 'onChange'
  });

  const watchedSkills = watch('required_skills') || [];
  const watchedSalaryMin = watch('salary_min');
  const watchedSalaryMax = watch('salary_max');
  const watchedCurrency = watch('currency');
  const watchedClient = watch('client');

  // Populate form when editing existing job
  useEffect(() => {
    if (editingJob && isOpen && !isLoadingCompanies) {
      // CRITICAL: Ensure companies are loaded before populating form
      if (companies.length === 0) {
        return;
      }
      
      // Parse salary range if it's a string like "$140,000 - $180,000"
      let salaryMin = 0;
      let salaryMax = 0;
      let currency = 'USD';
      
      if (editingJob.salary_range) {
        if (typeof editingJob.salary_range === 'string') {
          // Parse string format: "$140,000 - $180,000"
          const match = editingJob.salary_range.match(/\$?([\d,]+)\s*-\s*\$?([\d,]+)/);
          if (match) {
            salaryMin = parseInt(match[1].replace(/,/g, ''));
            salaryMax = parseInt(match[2].replace(/,/g, ''));
          }
        } else if (typeof editingJob.salary_range === 'object') {
          salaryMin = editingJob.salary_range.min || 0;
          salaryMax = editingJob.salary_range.max || 0;
          currency = editingJob.salary_range.currency || 'USD';
        }
      }
      
      // CRITICAL: Get client_company value
      const clientCompanyValue = editingJob.client_company || editingJob.client || '';
      
      const formValues = {
        client: clientCompanyValue,
        client_cost_center: editingJob.client_cost_center || '',
        workday_supervisory_org: editingJob.workday_supervisory_organization || editingJob.workday_supervisory_org || '',
        title: editingJob.title || '',
        department: editingJob.department || '',
        location: editingJob.location || '',
        employment_type: editingJob.employment_type || '',
        location_requirements: editingJob.location_requirements || '',
        experience_level: editingJob.experience_level || '',
        hiring_manager: editingJob.hiring_manager || '',
        account_manager: editingJob.account_manager || '',
        required_skills: editingJob.skills_required || editingJob.required_skills || [],
        nice_to_have_skills: editingJob.nice_to_have_skills || [],
        description: editingJob.description || '',
        responsibilities: editingJob.responsibilities || '',
        qualifications: editingJob.qualifications || '',
        benefits: editingJob.benefits || '',
        salary_min: salaryMin,
        salary_max: salaryMax,
        currency: currency,
        no_of_openings: editingJob.no_of_openings || 1,
        status: editingJob.status || 'accepting_candidates',
        travel: editingJob.travel || 'none',
        visa_sponsorship: editingJob.visa_sponsorship || 'no',
        relocation_allowed: editingJob.relocation_allowed || 'no'
      };
      
      // Reset form with values - this doesn't mark as dirty
      reset(formValues);
    }
  }, [editingJob, isOpen, companies, isLoadingCompanies, reset]);

  // Fetch companies on modal open
  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  // Auto-populate cost center and workday org when company changes
  useEffect(() => {
    if (watchedClient) {
      const selectedCompany = companies.find(c => c.name === watchedClient);
      if (selectedCompany) {
        setValue('client_cost_center', selectedCompany.cost_center);
        setValue('workday_supervisory_org', selectedCompany.workday_supervisory_org);
      }
    }
  }, [watchedClient, companies, setValue]);

  const fetchCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/client-companies`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }
      
      const data = await response.json();
      const allCompanies = data.companies || [];
      console.log('[FILTER] Total companies from API:', allCompanies.length);
      console.log('[FILTER] User:', selectedUser?.full_name, 'Role:', selectedUser?.role);
      console.log('[FILTER] Team Lead:', teamLead?.full_name);
      console.log('[FILTER] Team Lead assigned_clients:', teamLead?.assigned_clients);
      
      // Filter companies by user permissions
      const filteredCompanies = filterCompaniesByUser(allCompanies, selectedUser, teamLead);
      console.log('[FILTER] After filtering:', filteredCompanies.length, 'companies');
      console.log('[FILTER] Filtered companies:', filteredCompanies.map(c => c.name).join(', '));
      
      setCompanies(filteredCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setIsLoadingCompanies(false);
    }
  };
  
  // Helper function to filter companies by user permissions
  const filterCompaniesByUser = (companies, user, teamLead) => {
    if (!user) return [];
    
    // Admin sees all
    if (user.role === 'admin' || user.role === 'junoadmin') {
      return companies;
    }
    
    return companies.filter(company => {
      const companyName = company.name;
      
      // Check if user is account manager of this company
      if (company.account_manager === user.name || company.account_manager_id === user.id) {
        console.log(`[FILTER DEBUG] ${companyName}: MATCH (user is account manager)`);
        return true;
      }
      
      // Team inheritance: check if company is in team lead's assigned clients
      if (teamLead?.assigned_clients) {
        const match = teamLead.assigned_clients.some(assignedClient => {
          const firstWord = assignedClient.split(/\s+/)[0];
          const doesMatch = companyName.startsWith(firstWord) || companyName === assignedClient;
          if (doesMatch) {
            console.log(`[FILTER DEBUG] ${companyName}: MATCH (${assignedClient})`);
          }
          return doesMatch;
        });
        if (match) return true;
      }
      
      // If no matching rules, don't show the company
      console.log(`[FILTER DEBUG] ${companyName}: REJECTED (no matching rules)`);
      return false;
    });
  };

  const checkCompanyAssignment = async (companyName) => {
    if (!companyName || !selectedUser) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/customers`);
      if (!response.ok) return;
      
      const data = await response.json();
      const company = (data.customers || []).find(c => 
        (c.client_name || c.company_name) === companyName
      );
      
      if (!company) return;
      
      // Check if company is assigned to another user/team
      const isOwnCompany = 
        company.account_manager_id === selectedUser.id ||
        company.created_by === selectedUser.id ||
        company.assigned_recruiters?.some(ar => ar.recruiter_id === selectedUser.id);
      
      if (!isOwnCompany && company.account_manager) {
        // Get team info if available
        let teamInfo = '';
        if (company.account_manager_id) {
          try {
            const teamResponse = await fetch(`${backendUrl}/api/users`);
            if (teamResponse.ok) {
              const userData = await teamResponse.json();
              const accountMgr = (userData.users || []).find(u => u.id === company.account_manager_id);
              if (accountMgr) {
                teamInfo = accountMgr.team_name ? ` (Team: ${accountMgr.team_name})` : '';
              }
            }
          } catch (e) {
            console.error('Error fetching team info:', e);
          }
        }
        
        setCompanyAssignmentInfo({
          companyName: company.client_name || company.company_name,
          accountManager: company.account_manager,
          teamInfo: teamInfo
        });
        
        // Show toast notification
        toast.info(
          `Company "${companyName}" is assigned to ${company.account_manager}${teamInfo}. You can still create a job for this company.`,
          { duration: 6000 }
        );
      } else {
        setCompanyAssignmentInfo(null);
      }
    } catch (error) {
      console.error('Error checking company assignment:', error);
    }
  };
  
  const handleCompanyAdded = (newCompany) => {
    // Add new company to list
    setCompanies(prev => [...prev, newCompany].sort((a, b) => a.name.localeCompare(b.name)));
    // Auto-select the new company
    setValue('client', newCompany.name);
    setValue('client_cost_center', newCompany.cost_center);
    setValue('workday_supervisory_org', newCompany.workday_supervisory_org);
    toast.success('Company added and selected!');
  };

  // File upload handler
  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setIsUploading(true);
    setUploadedFile(file);
    setParseStatus(null);
    setParseMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/jobs/parse-job-description`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to parse job description');
      }

      const data = await response.json();
      
      if (data.success && data.job_data) {
        const parsedData = data.job_data;
        
        // Count how many fields were extracted
        let extractedFieldsCount = 0;
        const requiredFields = ['title', 'department', 'location', 'description'];
        let hasRequiredFields = 0;
        
        // Map parsed data to form fields and count extractions
        if (parsedData.client && parsedData.client.trim()) {
          setValue('client', parsedData.client);
          extractedFieldsCount++;
          hasRequiredFields++;
        }
        if (parsedData.client_cost_center && parsedData.client_cost_center.trim()) {
          setValue('client_cost_center', parsedData.client_cost_center);
          extractedFieldsCount++;
        }
        if (parsedData.workday_supervisory_org && parsedData.workday_supervisory_org.trim()) {
          setValue('workday_supervisory_org', parsedData.workday_supervisory_org);
          extractedFieldsCount++;
        }
        if (parsedData.title && parsedData.title.trim()) {
          setValue('title', parsedData.title);
          extractedFieldsCount++;
          hasRequiredFields++;
        }
        if (parsedData.department && parsedData.department.trim()) {
          setValue('department', parsedData.department);
          extractedFieldsCount++;
          hasRequiredFields++;
        }
        if (parsedData.location && parsedData.location.trim()) {
          setValue('location', parsedData.location);
          extractedFieldsCount++;
          hasRequiredFields++;
        }
        if (parsedData.employment_type && parsedData.employment_type.trim()) {
          setValue('employment_type', parsedData.employment_type);
          extractedFieldsCount++;
        }
        if (parsedData.location_requirements && parsedData.location_requirements.trim()) {
          setValue('location_requirements', parsedData.location_requirements);
          extractedFieldsCount++;
        }
        if (parsedData.experience_level && parsedData.experience_level.trim()) {
          setValue('experience_level', parsedData.experience_level);
          extractedFieldsCount++;
        }
        if (parsedData.hiring_manager && parsedData.hiring_manager.trim()) {
          setValue('hiring_manager', parsedData.hiring_manager);
          extractedFieldsCount++;
        }
        if (parsedData.account_manager && parsedData.account_manager.trim()) {
          setValue('account_manager', parsedData.account_manager);
          extractedFieldsCount++;
        }
        if (parsedData.description && parsedData.description.trim()) {
          setValue('description', parsedData.description);
          extractedFieldsCount++;
          hasRequiredFields++;
        }
        if (parsedData.responsibilities && parsedData.responsibilities.trim()) {
          setValue('responsibilities', parsedData.responsibilities);
          extractedFieldsCount++;
        }
        if (parsedData.qualifications && parsedData.qualifications.trim()) {
          setValue('qualifications', parsedData.qualifications);
          extractedFieldsCount++;
        }
        if (parsedData.benefits && parsedData.benefits.trim()) {
          setValue('benefits', parsedData.benefits);
          extractedFieldsCount++;
        }
        if (parsedData.currency && parsedData.currency.trim()) {
          setValue('currency', parsedData.currency);
          extractedFieldsCount++;
        }
        if (parsedData.salary_min) {
          setValue('salary_min', parsedData.salary_min);
          extractedFieldsCount++;
        }
        if (parsedData.salary_max) {
          setValue('salary_max', parsedData.salary_max);
          extractedFieldsCount++;
        }
        if (parsedData.travel && parsedData.travel.trim()) {
          setValue('travel', parsedData.travel);
          extractedFieldsCount++;
        }
        if (parsedData.visa_sponsorship && parsedData.visa_sponsorship.trim()) {
          setValue('visa_sponsorship', parsedData.visa_sponsorship);
          extractedFieldsCount++;
        }
        if (parsedData.relocation_allowed && parsedData.relocation_allowed.trim()) {
          setValue('relocation_allowed', parsedData.relocation_allowed);
          extractedFieldsCount++;
        }
        if (parsedData.no_of_openings) {
          setValue('no_of_openings', parsedData.no_of_openings);
          extractedFieldsCount++;
        }
        if (parsedData.required_skills && parsedData.required_skills.length > 0) {
          setValue('required_skills', parsedData.required_skills);
          extractedFieldsCount++;
        }

        // Determine parse status
        if (hasRequiredFields >= 3 && extractedFieldsCount >= 5) {
          // Good extraction
          setParseStatus('success');
          setParseMessage(`Successfully extracted ${extractedFieldsCount} fields from your document. Review and edit as needed.`);
          toast.success('Job description parsed successfully!');
        } else if (extractedFieldsCount > 0) {
          // Partial extraction
          setParseStatus('partial');
          setParseMessage(`Partially extracted ${extractedFieldsCount} fields. The document may not contain standard job description format. Please fill in the remaining fields manually.`);
          toast.warning('Partial data extracted. Please complete remaining fields.');
        } else {
          // No extraction
          setParseStatus('error');
          setParseMessage('Could not extract job information from this document. The file may not contain a job description or the format is not recognized. Please fill in the fields manually.');
          toast.error('No job data found in document');
        }
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      setParseStatus('error');
      setParseMessage(error.message || 'Failed to parse job description. Please try again or fill in the fields manually.');
      toast.error(error.message || 'Failed to parse job description');
    } finally {
      setIsUploading(false);
      setShowUploader(false);
    }
  };

  // Remove uploaded file and reset
  const handleRemoveFile = () => {
    setUploadedFile(null);
    setParseStatus(null);
    setParseMessage('');
  };

  // AI Refinement handler
  const handleRefineWithAI = async () => {
    setIsRefining(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Get current form values
      const currentValues = watch();
      
      // Prepare data for refinement
      const jobData = {
        title: currentValues.title || '',
        department: currentValues.department || '',
        location: currentValues.location || '',
        employment_type: currentValues.employment_type || '',
        experience_level: currentValues.experience_level || '',
        required_skills: currentValues.required_skills || [],
        description: currentValues.description || '',
        responsibilities: currentValues.responsibilities || '',
        qualifications: currentValues.qualifications || '',
        benefits: currentValues.benefits || ''
      };
      
      const response = await fetch(`${backendUrl}/api/jobs/refine-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jobData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to refine job details');
      }
      
      const data = await response.json();
      
      if (data.success && data.refined_data) {
        setRefinedData(data.refined_data);
        setShowRefinedSuggestions(true);
        toast.success('AI suggestions generated! Review and apply changes.');
      }
    } catch (error) {
      console.error('Error refining job details:', error);
      toast.error(error.message || 'Failed to refine job details');
    } finally {
      setIsRefining(false);
    }
  };

  // Accept individual refined field
  const acceptRefinedField = (fieldName, value) => {
    setValue(fieldName, value);
    toast.success(`${fieldName} updated!`);
  };

  // Accept all refined suggestions
  const acceptAllRefined = () => {
    if (refinedData) {
      if (refinedData.experience_level) setValue('experience_level', refinedData.experience_level);
      if (refinedData.required_skills && refinedData.required_skills.length > 0) {
        setValue('required_skills', refinedData.required_skills);
      }
      if (refinedData.description) setValue('description', refinedData.description);
      if (refinedData.responsibilities) setValue('responsibilities', refinedData.responsibilities);
      if (refinedData.qualifications) setValue('qualifications', refinedData.qualifications);
      if (refinedData.benefits) setValue('benefits', refinedData.benefits);
      
      setShowRefinedSuggestions(false);
      setRefinedData(null);
      toast.success('All AI suggestions applied!');
    }
  };

  // Dismiss refined suggestions
  const dismissRefined = () => {
    setShowRefinedSuggestions(false);
    setRefinedData(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxFiles: 1,
    multiple: false
  });

  // Add skill handler
  const addSkill = () => {
    if (currentSkill.trim() && !watchedSkills.includes(currentSkill.trim())) {
      setValue('required_skills', [...watchedSkills, currentSkill.trim()]);
      setCurrentSkill('');
    }
  };

  // Remove skill handler
  const removeSkill = (skillToRemove) => {
    setValue('required_skills', watchedSkills.filter(skill => skill !== skillToRemove));
  };

  // Form submit handler
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Transform data to match backend schema
      const jobPayload = {
        client: data.client,
        client_cost_center: data.client_cost_center,
        workday_supervisory_org: data.workday_supervisory_org,
        title: data.title,
        department: data.department,
        location: data.location,
        employment_type: data.employment_type,
        location_requirements: data.location_requirements,
        experience_level: data.experience_level,
        hiring_manager: data.hiring_manager,
        account_manager: data.account_manager || '',
        required_skills: data.required_skills,
        description: data.description,
        responsibilities: data.responsibilities || '',
        qualifications: data.qualifications || '',
        benefits: data.benefits || '',
        salary_range: {
          min: data.salary_min,
          max: data.salary_max,
          currency: data.currency
        },
        no_of_openings: data.no_of_openings,
        status: data.status,
        travel: data.travel,
        visa_sponsorship: data.visa_sponsorship,
        relocation_allowed: data.relocation_allowed,
        source: 'manual',
        // CRITICAL: Track who created this job
        created_by: selectedUser?.id || null,
        primary_recruiter_id: selectedUser?.id || null
      };

      // Use PUT for edit, POST for create
      const url = isEditMode 
        ? `${backendUrl}/api/jobs/${editingJob.id}` 
        : `${backendUrl}/api/jobs/manual`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jobPayload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `Failed to ${isEditMode ? 'update' : 'create'} job`);
      }

      const resultData = await response.json().catch(() => ({}));
      const createdJob = resultData.job || resultData;
      
      // Show success message
      if (isEditMode) {
        toast.success('Job requisition updated successfully!');
      } else {
        // Show success message with sub-jobs info for new jobs
        if (resultData.sub_jobs && resultData.sub_jobs.length > 0) {
          toast.success(
            `Job requisition created successfully with ${resultData.sub_jobs.length} sub-jobs!`,
            {
              description: `Sub-jobs: 1 of ${resultData.sub_jobs.length} through ${resultData.sub_jobs.length} of ${resultData.sub_jobs.length}`,
              duration: 5000
            }
          );
        } else {
          toast.success('Job requisition created successfully!');
        }
      }
      
      // Always reset form and close modal on success
      reset();
      setParseStatus(null);
      
      // Call onSuccess callback if provided (wrapped in try-catch to prevent blocking modal close)
      if (onSuccess) {
        try {
          onSuccess(createdJob);
        } catch (callbackError) {
          console.error('Error in onSuccess callback:', callbackError);
          // Don't let callback errors prevent modal from closing
        }
      }
      
      // ALWAYS close the modal on success
      onClose();
    } catch (error) {
      console.error('Error creating/updating job:', error);
      toast.error(error.message || 'Failed to create job requisition');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setUploadedFile(null);
    setParseStatus(null);
    setParseMessage('');
    setShowUploader(false);
    setRefinedData(null);
    setShowRefinedSuggestions(false);
    onClose();
  };

  return (
    <>
      {/* Add Company Modal */}
      <AddCompanyModal
        isOpen={showAddCompany}
        onClose={() => setShowAddCompany(false)}
        onSuccess={handleCompanyAdded}
      />

      {/* Job Creation Modal */}
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col" data-testid="job-creation-modal">
        {/* Sticky Header */}
        <div className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle className="text-2xl font-semibold text-primary">
            {isEditMode ? 'Edit Job Requisition' : 'Create Job Requisition'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground mt-2">
            {isEditMode 
              ? 'Update the job details below. Required fields are marked with *'
              : 'Fill in the details below to create a new job posting. Required fields are marked with *'
            }
          </DialogDescription>
        </div>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 overflow-auto">
          <div className="space-y-6 py-6 px-6">
            {/* Upload Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-foreground">Optional: Upload Job Description</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Upload a PDF, DOC, DOCX, or TXT file to auto-populate fields using AI
                  </p>
                </div>
                {!uploadedFile && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowUploader(!showUploader)}
                    data-testid="upload-job-description-button"
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Upload Job Description (Optional)
                  </Button>
                )}
              </div>

              {/* Uploaded File Display */}
              {uploadedFile && (
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg border" data-testid="uploaded-file-info">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{uploadedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(uploadedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isUploading && (
                      <Loader2 className="h-4 w-4 text-primary animate-spin" />
                    )}
                    {!isUploading && (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowUploader(true)}
                          data-testid="upload-another-button"
                        >
                          Upload Another
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveFile}
                          data-testid="remove-file-button"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* File Uploader (shown only when button clicked) */}
              {showUploader && !isUploading && (
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragActive
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="file-dropzone"
                >
                  <input {...getInputProps()} />
                  <div className="flex flex-col items-center gap-3">
                    <FileText className="h-10 w-10 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Drop your job description here, or click to browse
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Supports PDF, DOC, DOCX, TXT (max 10MB)
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Parsing in Progress */}
              {isUploading && (
                <Alert className="bg-primary/5 border-primary/20">
                  <Loader2 className="h-4 w-4 text-primary animate-spin" />
                  <AlertDescription className="text-foreground">
                    Parsing your job description using AI... This may take a moment.
                  </AlertDescription>
                </Alert>
              )}

              {/* Parse Status Banners */}
              {parseStatus === 'success' && (
                <Alert className="bg-success/10 border-success" data-testid="parse-success-banner">
                  <Check className="h-4 w-4 text-success" />
                  <AlertDescription className="text-success">
                    {parseMessage}
                  </AlertDescription>
                </Alert>
              )}

              {parseStatus === 'partial' && (
                <Alert className="bg-yellow-50 border-yellow-400 dark:bg-yellow-900/10 dark:border-yellow-600" data-testid="parse-partial-banner">
                  <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  <AlertDescription className="text-yellow-800 dark:text-yellow-300">
                    {parseMessage}
                  </AlertDescription>
                </Alert>
              )}

              {parseStatus === 'error' && (
                <Alert className="bg-destructive/10 border-destructive" data-testid="parse-error-banner">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <AlertDescription className="text-destructive">
                    {parseMessage}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <Separator />

            {/* Form Sections */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Section 1: Basic Information */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-foreground">Basic Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Client Company */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Client Company <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="client"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Select 
                              value={field.value} 
                              onValueChange={(value) => {
                                field.onChange(value);
                                checkCompanyAssignment(value);
                              }}
                              disabled={isLoadingCompanies}
                            >
                              <SelectTrigger
                                className={errors.client ? 'border-destructive flex-1' : 'flex-1'}
                                data-testid="client-select"
                                aria-required="true"
                              >
                                <SelectValue placeholder={isLoadingCompanies ? "Loading companies..." : "Select a company"} />
                              </SelectTrigger>
                              <SelectContent>
                                {companies.length === 0 && !isLoadingCompanies && (
                                  <div className="p-2 text-sm text-muted-foreground text-center">
                                    No companies found
                                  </div>
                                )}
                                {companies.map((company) => (
                                  <SelectItem key={company.id} value={company.name}>
                                    {company.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowAddCompany(true)}
                              data-testid="add-company-button"
                              className="flex items-center gap-2"
                            >
                              <Building2 className="h-4 w-4" />
                              Add New
                            </Button>
                          </div>
                          {errors.client && (
                            <p className="text-xs text-destructive">{errors.client.message}</p>
                          )}
                          
                          {/* Company Assignment Info */}
                          {companyAssignmentInfo && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-blue-800">
                                  <p className="font-medium">Company Assignment Info</p>
                                  <p className="mt-1">
                                    "{companyAssignmentInfo.companyName}" is currently managed by{' '}
                                    <strong>{companyAssignmentInfo.accountManager}</strong>
                                    {companyAssignmentInfo.teamInfo}.
                                  </p>
                                  <p className="mt-1 text-xs">
                                    You can still create a job for this company if you have the necessary permissions.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <p className="text-xs text-muted-foreground">
                            Select from existing companies. Cost Center and Workday Org will auto-populate.
                          </p>
                        </div>
                      )}
                    />
                  </div>

                  {/* Client Cost Center */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Client Cost Center <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="client_cost_center"
                      control={control}
                      render={({ field }) => {
                        const selectedCompany = companies.find(c => c.name === watchedClient);
                        const isAutoPopulated = selectedCompany && field.value === selectedCompany.cost_center;
                        
                        return (
                          <div>
                            <Input
                              {...field}
                              placeholder="Auto-populated from selected company"
                              className={`${errors.client_cost_center ? 'border-destructive' : ''} ${isAutoPopulated ? 'bg-muted' : ''}`}
                              data-testid="cost-center-input"
                              aria-required="true"
                              readOnly={isAutoPopulated}
                            />
                            {errors.client_cost_center && (
                              <p className="text-xs text-destructive mt-1">{errors.client_cost_center.message}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {isAutoPopulated ? '✓ Auto-populated from company' : 'Required for finance reports'}
                            </p>
                          </div>
                        );
                      }}
                    />
                  </div>

                  {/* Workday Supervisory Organization */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Workday Supervisory Organization <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="workday_supervisory_org"
                      control={control}
                      render={({ field }) => {
                        const selectedCompany = companies.find(c => c.name === watchedClient);
                        const isAutoPopulated = selectedCompany && field.value === selectedCompany.workday_supervisory_org;
                        
                        return (
                          <div>
                            <Input
                              {...field}
                              placeholder="Auto-populated from selected company"
                              className={`${errors.workday_supervisory_org ? 'border-destructive' : ''} ${isAutoPopulated ? 'bg-muted' : ''}`}
                              data-testid="workday-org-input"
                              aria-required="true"
                              readOnly={isAutoPopulated}
                            />
                            {errors.workday_supervisory_org && (
                              <p className="text-xs text-destructive mt-1">{errors.workday_supervisory_org.message}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {isAutoPopulated ? '✓ Auto-populated from company' : 'Required for Workday sync, HR, and billing'}
                            </p>
                          </div>
                        );
                      }}
                    />
                  </div>

                  {/* Job Title */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Job Title <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <Input
                            {...field}
                            placeholder="e.g., Senior React Developer"
                            className={errors.title ? 'border-destructive' : ''}
                            data-testid="job-title-input"
                            aria-required="true"
                          />
                          {errors.title && (
                            <p className="text-xs text-destructive mt-1">{errors.title.message}</p>
                          )}
                          {!errors.title && field.value.length >= 3 && (
                            <Check className="h-4 w-4 text-success absolute right-3 top-3" />
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Department */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Department <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="department"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <Input
                            {...field}
                            placeholder="e.g., Engineering"
                            className={errors.department ? 'border-destructive' : ''}
                            data-testid="department-input"
                            aria-required="true"
                          />
                          {errors.department && (
                            <p className="text-xs text-destructive mt-1">{errors.department.message}</p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Location <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="location"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <Input
                            {...field}
                            placeholder="e.g., San Francisco, CA or Remote"
                            className={errors.location ? 'border-destructive' : ''}
                            data-testid="location-input"
                            aria-required="true"
                          />
                          {errors.location && (
                            <p className="text-xs text-destructive mt-1">{errors.location.message}</p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Employment Type */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Employment Type <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="employment_type"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger
                            className={errors.employment_type ? 'border-destructive' : ''}
                            data-testid="employment-type-select"
                            aria-required="true"
                          >
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {EMPLOYMENT_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.employment_type && (
                      <p className="text-xs text-destructive mt-1">{errors.employment_type.message}</p>
                    )}
                  </div>

                  {/* Location Requirements */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Location Requirements <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="location_requirements"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger
                            className={errors.location_requirements ? 'border-destructive' : ''}
                            data-testid="location-requirements-select"
                            aria-required="true"
                          >
                            <SelectValue placeholder="Select requirement" />
                          </SelectTrigger>
                          <SelectContent>
                            {LOCATION_REQUIREMENTS.map((req) => (
                              <SelectItem key={req.value} value={req.value}>
                                {req.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Section 2: Hiring Team */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-foreground">Hiring Team</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Hiring Manager */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Hiring Manager <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="hiring_manager"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <Input
                            {...field}
                            placeholder="e.g., John Doe"
                            className={errors.hiring_manager ? 'border-destructive' : ''}
                            data-testid="hiring-manager-input"
                            aria-required="true"
                          />
                          {errors.hiring_manager && (
                            <p className="text-xs text-destructive mt-1">{errors.hiring_manager.message}</p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Account Manager */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Account Manager <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="account_manager"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="e.g., Jane Smith"
                          data-testid="account-manager-input"
                          className={errors.account_manager ? 'border-red-500' : ''}
                        />
                      )}
                    />
                    {errors.account_manager && (
                      <p className="text-sm text-red-500 mt-1">{errors.account_manager.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Section 3: Job Details */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-foreground">Job Details</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRefineWithAI}
                    disabled={isRefining || !watch('title')}
                    data-testid="refine-with-ai-button"
                    className="flex items-center gap-2"
                  >
                    {isRefining ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Refining...
                      </>
                    ) : (
                      <>
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Refine with AI
                      </>
                    )}
                  </Button>
                </div>
                
                {/* AI Refinement Tip */}
                {!showRefinedSuggestions && (
                  <p className="text-xs text-muted-foreground italic">
                    💡 Use "Refine with AI" to enhance job details with industry-standard descriptions, skills, and requirements
                  </p>
                )}
                
                {/* AI Refined Suggestions Panel */}
                {showRefinedSuggestions && refinedData && (
                  <Alert className="bg-primary/5 border-primary/30" data-testid="ai-suggestions-panel">
                    <Check className="h-4 w-4 text-primary" />
                    <AlertDescription>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-foreground">AI-Generated Suggestions</p>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="default"
                              size="sm"
                              onClick={acceptAllRefined}
                              data-testid="accept-all-suggestions-button"
                            >
                              Accept All
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={dismissRefined}
                              data-testid="dismiss-suggestions-button"
                            >
                              Dismiss
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Review the AI-generated suggestions below. You can accept individual fields or all at once.
                        </p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Experience Level */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Experience Level <span className="text-destructive">*</span>
                  </label>
                  <Controller
                    name="experience_level"
                    control={control}
                    render={({ field }) => (
                      <div className="space-y-2">
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger
                            className={errors.experience_level ? 'border-destructive' : ''}
                            data-testid="experience-level-select"
                            aria-required="true"
                          >
                            <SelectValue placeholder="Select experience level" />
                          </SelectTrigger>
                          <SelectContent>
                            {EXPERIENCE_LEVELS.map((level) => (
                              <SelectItem key={level.value} value={level.value}>
                                {level.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {refinedData?.experience_level && refinedData.experience_level !== field.value && (
                          <div className="p-2 bg-accent/10 border border-accent/30 rounded text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">AI suggests: <strong>{refinedData.experience_level}</strong></span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => acceptRefinedField('experience_level', refinedData.experience_level)}
                                className="h-6 text-xs"
                              >
                                Apply
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  />
                  {errors.experience_level && (
                    <p className="text-xs text-destructive mt-1">{errors.experience_level.message}</p>
                  )}
                </div>

                {/* Required Skills */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Required Skills <span className="text-destructive">*</span>
                  </label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={currentSkill}
                      onChange={(e) => setCurrentSkill(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addSkill();
                        }
                      }}
                      placeholder="Type a skill and press Enter or click Add"
                      data-testid="skill-input"
                    />
                    <Button
                      type="button"
                      onClick={addSkill}
                      variant="outline"
                      size="sm"
                      data-testid="add-skill-button"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {watchedSkills.map((skill, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1"
                        data-testid={`skill-badge-${index}`}
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill(skill)}
                          className="ml-1 hover:text-destructive"
                          aria-label={`Remove ${skill}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  {refinedData?.required_skills && refinedData.required_skills.length > 0 && (
                    <div className="mt-2 p-3 bg-accent/10 border border-accent/30 rounded">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">AI suggested skills:</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => acceptRefinedField('required_skills', refinedData.required_skills)}
                          className="h-6 text-xs"
                        >
                          Apply All
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {refinedData.required_skills.map((skill, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {errors.required_skills && (
                    <p className="text-xs text-destructive mt-2">{errors.required_skills.message}</p>
                  )}
                </div>

                {/* Job Description */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Job Description <span className="text-destructive">*</span>
                  </label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <div className="space-y-2">
                        <Textarea
                          {...field}
                          placeholder="Provide a detailed job description..."
                          rows={5}
                          className={errors.description ? 'border-destructive' : ''}
                          data-testid="description-textarea"
                          aria-required="true"
                        />
                        {refinedData?.description && (
                          <div className="p-3 bg-accent/10 border border-accent/30 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-muted-foreground">AI suggestion:</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => acceptRefinedField('description', refinedData.description)}
                                className="h-6 text-xs"
                              >
                                Apply
                              </Button>
                            </div>
                            <p className="text-xs text-foreground whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {refinedData.description}
                            </p>
                          </div>
                        )}
                        {errors.description && (
                          <p className="text-xs text-destructive mt-1">{errors.description.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* Key Responsibilities */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Key Responsibilities
                  </label>
                  <Controller
                    name="responsibilities"
                    control={control}
                    render={({ field }) => (
                      <div className="space-y-2">
                        <Textarea
                          {...field}
                          placeholder="List key responsibilities (optional)..."
                          rows={4}
                          data-testid="responsibilities-textarea"
                        />
                        {refinedData?.responsibilities && (
                          <div className="p-3 bg-accent/10 border border-accent/30 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-muted-foreground">AI suggestion:</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => acceptRefinedField('responsibilities', refinedData.responsibilities)}
                                className="h-6 text-xs"
                              >
                                Apply
                              </Button>
                            </div>
                            <p className="text-xs text-foreground whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {refinedData.responsibilities}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* Required Qualifications */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Required Qualifications
                  </label>
                  <Controller
                    name="qualifications"
                    control={control}
                    render={({ field }) => (
                      <div className="space-y-2">
                        <Textarea
                          {...field}
                          placeholder="List required qualifications (optional)..."
                          rows={4}
                          data-testid="qualifications-textarea"
                        />
                        {refinedData?.qualifications && (
                          <div className="p-3 bg-accent/10 border border-accent/30 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-muted-foreground">AI suggestion:</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => acceptRefinedField('qualifications', refinedData.qualifications)}
                                className="h-6 text-xs"
                              >
                                Apply
                              </Button>
                            </div>
                            <p className="text-xs text-foreground whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {refinedData.qualifications}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* Benefits & Perks */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Benefits & Perks
                  </label>
                  <Controller
                    name="benefits"
                    control={control}
                    render={({ field }) => (
                      <div className="space-y-2">
                        <Textarea
                          {...field}
                          placeholder="List benefits and perks (optional)..."
                          rows={3}
                          data-testid="benefits-textarea"
                        />
                        {refinedData?.benefits && (
                          <div className="p-3 bg-accent/10 border border-accent/30 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-muted-foreground">AI suggestion:</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => acceptRefinedField('benefits', refinedData.benefits)}
                                className="h-6 text-xs"
                              >
                                Apply
                              </Button>
                            </div>
                            <p className="text-xs text-foreground whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {refinedData.benefits}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Section 4: Compensation */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-foreground">Compensation</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Currency <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="currency"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="currency-select">
                            <SelectValue placeholder="Currency" />
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENCIES.map((curr) => (
                              <SelectItem key={curr.value} value={curr.value}>
                                {curr.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {/* Minimum Salary */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Minimum Salary
                    </label>
                    <Controller
                      name="salary_min"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          placeholder="e.g., 80000"
                          onChange={(e) => {
                            const value = e.target.value ? parseInt(e.target.value) : null;
                            // Prevent negative values
                            if (value !== null && value < 0) {
                              field.onChange(0);
                            } else {
                              field.onChange(value);
                            }
                          }}
                          data-testid="salary-min-input"
                        />
                      )}
                    />
                  </div>

                  {/* Maximum Salary */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Maximum Salary
                    </label>
                    <Controller
                      name="salary_max"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <Input
                            {...field}
                            type="number"
                            min="0"
                            placeholder="e.g., 120000"
                            onChange={(e) => {
                              const value = e.target.value ? parseInt(e.target.value) : null;
                              // Prevent negative values
                              if (value !== null && value < 0) {
                                field.onChange(0);
                              } else {
                                field.onChange(value);
                              }
                            }}
                            className={errors.salary_max ? 'border-destructive' : ''}
                            data-testid="salary-max-input"
                          />
                          {errors.salary_max && (
                            <p className="text-xs text-destructive mt-1">{errors.salary_max.message}</p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>

                {/* Salary Range Summary */}
                {(watchedSalaryMin || watchedSalaryMax) && (
                  <div className={`p-3 rounded-md ${
                    watchedSalaryMin && watchedSalaryMax && watchedSalaryMax < watchedSalaryMin
                      ? 'bg-red-50 border border-red-200'
                      : 'bg-muted'
                  }`}>
                    <p className="text-sm text-muted-foreground">
                      Salary Range:{' '}
                      <span className={`font-medium ${
                        watchedSalaryMin && watchedSalaryMax && watchedSalaryMax < watchedSalaryMin
                          ? 'text-red-700'
                          : 'text-foreground'
                      }`}>
                        {watchedSalaryMin && watchedSalaryMax
                          ? `${watchedCurrency} ${watchedSalaryMin.toLocaleString()} - ${watchedSalaryMax.toLocaleString()}`
                          : watchedSalaryMin
                          ? `${watchedCurrency} ${watchedSalaryMin.toLocaleString()}+`
                          : `Up to ${watchedCurrency} ${watchedSalaryMax.toLocaleString()}`}
                      </span>
                    </p>
                    {watchedSalaryMin && watchedSalaryMax && watchedSalaryMax < watchedSalaryMin && (
                      <div className="mt-2 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700">
                          <span className="font-semibold">Invalid range:</span> Maximum salary must be greater than or equal to minimum salary.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Separator />

              {/* Section 5: Logistics & Work Arrangement */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-foreground">Logistics & Work Arrangement</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Number of Openings */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                      Number of Positions <span className="text-destructive">*</span>
                      <div className="relative group">
                        <AlertCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-3 bg-popover border border-border rounded-md shadow-lg text-xs text-popover-foreground z-50">
                          <p className="font-semibold mb-1">Multiple Positions</p>
                          <p>If you select more than 1 position, we'll automatically create sub-jobs (e.g., "1 of 4") for better tracking. Each sub-job can be managed independently while linking back to the main job posting.</p>
                        </div>
                      </div>
                    </label>
                    <Controller
                      name="no_of_openings"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <Input
                            {...field}
                            type="number"
                            min="1"
                            placeholder="e.g., 1"
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 1;
                              if (value < 1) {
                                field.onChange(1);
                              } else {
                                field.onChange(value);
                              }
                            }}
                            className={errors.no_of_openings ? 'border-destructive' : ''}
                            data-testid="openings-input"
                            aria-required="true"
                          />
                          {errors.no_of_openings && (
                            <p className="text-xs text-destructive mt-1">{errors.no_of_openings.message}</p>
                          )}
                          {watch('no_of_openings') > 1 && (
                            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-start gap-2">
                              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium">Sub-jobs will be created</p>
                                <p className="mt-0.5">
                                  {watch('no_of_openings')} sub-jobs will be created: "1 of {watch('no_of_openings')}", "2 of {watch('no_of_openings')}", etc.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Job Status */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Job Status <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="status-select">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            {JOB_STATUSES.map((status) => (
                              <SelectItem key={status.value} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {/* Travel Required */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Travel Required
                    </label>
                    <Controller
                      name="travel"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="travel-select">
                            <SelectValue placeholder="Select travel %" />
                          </SelectTrigger>
                          <SelectContent>
                            {TRAVEL_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {/* Visa Sponsorship */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Visa Sponsorship Provided
                    </label>
                    <Controller
                      name="visa_sponsorship"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="visa-sponsorship-select">
                            <SelectValue placeholder="Yes/No" />
                          </SelectTrigger>
                          <SelectContent>
                            {YES_NO_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {/* Relocation Assistance */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Relocation Assistance
                    </label>
                    <Controller
                      name="relocation_allowed"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="relocation-select">
                            <SelectValue placeholder="Yes/No" />
                          </SelectTrigger>
                          <SelectContent>
                            {YES_NO_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions - Moved inside form */}
              <div className="flex items-center justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  data-testid="cancel-button"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                {/* Save Draft button hidden as requested */}
                <Button
                  type="submit"
                  disabled={isSubmitting || (isEditMode && !isDirty)}
                  data-testid="create-requisition-button"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    isEditMode ? 'Update Requisition' : 'Create Requisition'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </ScrollArea>

        {/* Footer - Now empty but kept for layout */}
        <div className="px-6 py-4 border-t flex-shrink-0 bg-background">
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default JobCreationModal;
